import {useAccessToken} from "../../services/auth"
import * as Sentry from "@sentry/react"
import React, {useState, useEffect, useRef} from "react"
import {
	getEmailTemplatesList,
	EmailTemplate,
	publishEmailTemplateVersion,
	createEmailTemplateVersion,
	createEmailTemplate,
} from "../../resources/emailTemplates"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {
	DataTable,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../../components/DataTable/DataTable"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import Button from "../../components/Button/Button"
import {DataTableActionButton, DataTableActionItem} from "../../components/DataTable/DataTableActionButton"
import Tag from "../../components/Tag/Tag"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useNavigate} from "react-router-dom"
import TitleBar from "../../components/TitleBar/TitleBar"
import {DefaultErr} from "../../components/Err/Err"
import MustacheEditor from "../../components/MustacheEditor/MustacheEditor"

export default function EmailTemplates({refresh}: {refresh: () => void}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const result = useAsyncResult(() => getEmailTemplatesList(accessToken), [accessToken, refresh])
	const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
	const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
	const [isCreating, setIsCreating] = useState(false)
	const [newTemplateId, setNewTemplateId] = useState("")
	const [newTemplateSubject, setNewTemplateSubject] = useState("")
	const [error, setError] = useState<string | null>(null)
	const [forcePublish, setForcePublish] = useState(false)
	const [, setIsSubmitting] = useState(false)

	const [publishStatus, publishTemplate, resetPublishStatus] = useAsyncResultIdle(
		(templateId: string, version: number) => publishEmailTemplateVersion(accessToken, {templateId, version})
	)

	// Track previous publish status to avoid infinite loops
	const publishStatusRef = useRef(publishStatus)

	useEffect(() => {
		// Only refresh if we have a new successful result and it's different from the previous one
		const prevStatus = publishStatusRef.current
		const isPrevSuccess = prevStatus && "value" in prevStatus
		const isCurrentSuccess = publishStatus && "value" in publishStatus

		if (isCurrentSuccess && (!isPrevSuccess || prevStatus !== publishStatus)) {
			refresh()
			// Reset status after handling to prevent re-triggering
			resetPublishStatus()
		}

		// Update the ref with current status
		publishStatusRef.current = publishStatus
	}, [publishStatus, refresh, resetPublishStatus])

	// Simple formatting function for dates
	const formatDate = (date: Date): string => {
		return date.toLocaleDateString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		})
	}

	const viewTemplateVersions = (template: EmailTemplate) => {
		navigate(`/admin/email-templates/${template.attributes.templateId}`)
	}

	const startEditing = (template: EmailTemplate) => {
		setIsCreating(false)
		setEditingTemplateId(template.id)
		setEditingTemplate(template)
		setForcePublish(false)
		setError(null)
	}

	// Reset error when editingTemplate changes
	useEffect(() => {
		if (editingTemplate) {
			setError(null)
		}
	}, [editingTemplate])

	const startCreating = () => {
		setEditingTemplateId(null)
		setEditingTemplate(null)
		setNewTemplateId("")
		setNewTemplateSubject("")
		setIsCreating(true)
		setForcePublish(false)
	}

	const cancelOperation = () => {
		setEditingTemplateId(null)
		setEditingTemplate(null)
		setIsCreating(false)
		setNewTemplateId("")
		setNewTemplateSubject("")
		setError(null)
		setForcePublish(false)
	}

	const handleSaveTemplate = async (htmlContent: string, subject?: string, samples?: string) => {
		if (!editingTemplate) return

		setIsSubmitting(true)
		setError(null)

		try {
			console.log("Saving template with content length:", htmlContent.length)
			await createEmailTemplateVersion(accessToken, {
				subject: subject || editingTemplate.attributes.subject,
				htmlContent,
				templateId: editingTemplate.attributes.templateId,
				forcePublish: forcePublish,
				samples: samples || "[]",
			})

			setEditingTemplateId(null)
			setEditingTemplate(null)
			setForcePublish(false)
			refresh()
		} catch (err) {
			console.error("Failed to update template:", err)
			setError("Failed to update template: " + (err instanceof Error ? err.message : String(err)))
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleCreateTemplate = async (htmlContent: string, subject?: string, samples?: string) => {
		if (!newTemplateId) {
			setError("Template ID is required")
			return
		}

		if (!subject) {
			setError("Email subject is required")
			return
		}

		setIsSubmitting(true)
		setError(null)

		try {
			console.log("Creating template with content length:", htmlContent.length)
			await createEmailTemplate(accessToken, {
				subject: subject,
				htmlContent,
				templateId: newTemplateId,
				samples: samples || "[]",
			})

			setIsCreating(false)
			setNewTemplateId("")
			setNewTemplateSubject("")
			refresh()
		} catch (err) {
			console.error("Failed to create template:", err)
			setError("Failed to create template: " + (err instanceof Error ? err.message : String(err)))
		} finally {
			setIsSubmitting(false)
		}
	}

	const renderCreateForm = () => {
		return (
			<div className="email-template-editor-container">
				<div className="email-template-editor-header">
					<h2>Create New Email Template</h2>
					<div className="template-form-fields">
						<div className="form-field">
							<label htmlFor="templateId">Template ID</label>
							<input
								type="text"
								id="templateId"
								value={newTemplateId}
								onChange={(e) => setNewTemplateId(e.target.value)}
								placeholder="Enter a unique template ID"
								required
								pattern="[a-zA-Z0-9_\-.]+"
								title="Only letters, numbers, underscores, hyphens, and dots are allowed"
							/>
						</div>
					</div>
					{error && <div className="error-message">{error}</div>}
				</div>
				<Sentry.ErrorBoundary fallback={<DefaultErr />}>
					<MustacheEditor
						initialHtml=""
						onSave={handleCreateTemplate}
						initialSubject={newTemplateSubject}
						showSubjectField={true}
					/>
				</Sentry.ErrorBoundary>
			</div>
		)
	}

	return (
		<div className={"email-templates columns"}>
			<DataTableCard className={"email-templates-card column is-12"}>
				<TitleBar title="Email Templates">
					{!editingTemplateId && !isCreating ? (
						<Button theme={"neutral"} prefixIcon={"add-plus-small"} onClick={startCreating} className={"ml-4"}>
							New
						</Button>
					) : (
						<Button theme={"neutral"} onClick={cancelOperation} className={"ml-4"}>
							Cancel
						</Button>
					)}
				</TitleBar>

				{isCreating ? (
					renderCreateForm()
				) : editingTemplateId && editingTemplate ? (
					<div className="email-template-editor-container">
						<div className="email-template-editor-header">
							<h2>Editing Template: {editingTemplate.attributes.subject}</h2>
							<p>Template ID: {editingTemplate.attributes.templateId}</p>
							<p>Current Version: {editingTemplate.attributes.version}</p>
							<div className="publish-option">
								<label>
									<input type="checkbox" checked={forcePublish} onChange={(e) => setForcePublish(e.target.checked)} />
									<span> Publish this version immediately</span>
								</label>
							</div>
							{error && <div className="error-message">{error}</div>}
						</div>

						<MustacheEditor
							initialHtml={editingTemplate.attributes.htmlContent}
							onSave={handleSaveTemplate}
							initialSubject={editingTemplate.attributes.subject}
							showSubjectField={true}
							samples={editingTemplate.attributes.samples}
						/>
					</div>
				) : (
					<AsyncResultComponent asyncResult={result} pendingComponent={<TablePending />}>
						{({value: emailTemplatesResult}) => {
							// Group templates by templateId to show only the latest version in the main list
							const templates = emailTemplatesResult.data || []

							// Process the array to group by templateId and keep only the latest version
							const groupedTemplates = Array.isArray(templates)
								? templates.reduce((acc: EmailTemplate[], template: EmailTemplate) => {
										const existingTemplate = acc.find(
											(t: EmailTemplate) => t.attributes.templateId === template.attributes.templateId
										)
										if (!existingTemplate || template.attributes.version > existingTemplate.attributes.version) {
											// Remove existing template if this one is newer
											const filteredAcc = existingTemplate
												? acc.filter((t: EmailTemplate) => t.attributes.templateId !== template.attributes.templateId)
												: acc
											// Add this template
											return [...filteredAcc, template]
										}
										return acc
								  }, [] as EmailTemplate[])
								: []

							return (
								<DataTable>
									<DataTableHead>
										<DataTableRow>
											<th>ID</th>
											<th>Template ID</th>
											<th>Subject</th>
											<th>Latest Version</th>
											<th>Published</th>
											<th>Created At</th>
											<th></th>
										</DataTableRow>
									</DataTableHead>
									<DataTableBody>
										{groupedTemplates.map((template: EmailTemplate) => (
											<DataTableRow
												key={template.id}
												onClick={() => viewTemplateVersions(template)}
												className="clickable-row"
											>
												<DataTableCell>{template.id}</DataTableCell>
												<DataTableCell>{template.attributes.templateId}</DataTableCell>
												<DataTableCell>{template.attributes.subject}</DataTableCell>
												<DataTableCell>{template.attributes.version}</DataTableCell>
												<DataTableCell>
													{template.attributes.isPublished ? (
														<Tag size={"small"} content={"Published"} variant={"outline"} theme={"success"} />
													) : (
														<Tag size={"small"} content={"Draft"} variant={"outline"} theme={"dark"} />
													)}
												</DataTableCell>
												<DataTableCell>{formatDate(new Date(template.attributes.createdAt))}</DataTableCell>
												<DataTableActionButton enableActions={true}>
													<DataTableActionItem
														onClick={(e?: React.MouseEvent) => {
															if (e) e.stopPropagation()
															startEditing(template)
														}}
														title={"Edit"}
														icon={"pencil-1--interface-essential"}
													/>
													{!template.attributes.isPublished && (
														<DataTableActionItem
															onClick={(e?: React.MouseEvent) => {
																if (e) e.stopPropagation()
																publishTemplate(template.attributes.templateId, template.attributes.version)
															}}
															title={"Publish"}
															icon={"email-send-1--emails"}
														/>
													)}
												</DataTableActionButton>
											</DataTableRow>
										))}
									</DataTableBody>
								</DataTable>
							)
						}}
					</AsyncResultComponent>
				)}
			</DataTableCard>
		</div>
	)
}
